import { useQuery } from '@tanstack/react-query';
import { createApiClient } from '../api/client';
import { useAccessToken } from '../auth/useAuth';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { workspaceKey } from './workspaceQueryKey';
import { isAdvisorWithoutAdmin } from './cashFlowAccess';
import { CASH_FLOW_AGREEMENT_CAPABILITY } from '@steward-app/steward-types';

/**
 * Probes `GET /me/capabilities?capabilities=cash-flow:agreement` for the ACTIVE workspace: the
 * switch behind the Cash flow nav item (steward-app#2592). Follows `useCanReadCredentials`: a thin
 * passthrough to appcore's own `evaluateRoleGrant`, never a mirrored role list.
 *
 * Why `cash-flow:agreement` and not `cash-flow:read`: a family admin at level "none" holds the
 * agreement capability (through `steward-admin`) but not `cash-flow:read`, and still needs the
 * Setup page to see whether cash flow is on and to turn it on. Every level that can read cash flow
 * also holds the agreement capability (`packRoles.ts`, the two family viewer roles), so this one
 * probe covers admins and viewers alike.
 *
 * UX gating only, never authorization: every `/cash-flow/*` route is gated by appcore itself.
 *
 * Unlike the credentials probe this FAILS CLOSED (`false` while loading and on any error). That
 * is the cheaper mistake for a brand-new item: a pillar that appears and then vanishes, or one
 * shown to a person whose workspace cannot answer, is worse than one that appears a moment late.
 */
export function useCanReadCashFlow(): boolean {
  const token = useAccessToken();
  const { activeWorkspaceId, activeMembership } = useWorkspace();

  const { data } = useQuery({
    queryKey: workspaceKey('capabilities', activeWorkspaceId, CASH_FLOW_AGREEMENT_CAPABILITY),
    queryFn: () =>
      createApiClient(token!).get<{ capabilities: Record<string, boolean> }>(
        `/me/capabilities?capabilities=${encodeURIComponent(CASH_FLOW_AGREEMENT_CAPABILITY)}`,
      ),
    enabled: !!token && !!activeWorkspaceId,
    staleTime: 5 * 60_000,
  });

  // A firm advisor (not an admin) holds the agreement capability but has no family level.
  if (activeMembership && isAdvisorWithoutAdmin(activeMembership.roleIds)) return false;
  return data?.capabilities?.[CASH_FLOW_AGREEMENT_CAPABILITY] === true;
}
